import { motion } from "framer-motion";
import React, { useState } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import {
  Plus,
  Edit,
  Trash2,
  TrendingUp,
  Search,
  Eye,
  Loader2,
  AlertCircle,
  Building2,
  Target,
  DollarSign,
  Calendar,
  Star,
  Filter,
} from "lucide-react";

import {
  useProjects,
  useDeleteProject,
  type Project,
} from "@/services/projectService";
import type { RootState } from "@/store";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import ProjectProgressModal from "@/components/admin/ProjectProgressModal";

interface ProjectsQueryParams {
  per_page?: number;
  page?: number;
  locale?: string;
  search?: string;
  status?: string;
}

const Projects: React.FC = () => {
  const { t } = useTranslation();
  const { currentLanguage } = useSelector((state: RootState) => state.language);
  const navigate = useNavigate();

  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [isProgressModalOpen, setIsProgressModalOpen] = useState(false);
  const [selectedProjectForProgress, setSelectedProjectForProgress] =
    useState<Project | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [projectToDelete, setProjectToDelete] = useState<Project | null>(null);

  const queryParams: ProjectsQueryParams = {
    per_page: 10,
    page: currentPage,
    locale: currentLanguage,
    ...(searchTerm && { search: searchTerm }),
    ...(statusFilter && { status: statusFilter }),
  };

  const { data: projectsData, isLoading, error } = useProjects(queryParams);

  const deleteProjectMutation = useDeleteProject();

  const projects = projectsData?.projects || [];
  const pagination = projectsData?.pagination;

  const handleDeleteProject = (project: Project) => {
    setProjectToDelete(project);
    setIsDeleteDialogOpen(true);
  };

  const confirmDeleteProject = async () => {
    if (!projectToDelete) return;

    try {
      await deleteProjectMutation.mutateAsync(projectToDelete.id);
      setIsDeleteDialogOpen(false);
      setProjectToDelete(null);
      // Success feedback is handled by the mutation's onSuccess callback in projectService
    } catch (error) {
      console.error("Failed to delete project:", error);
      // Error feedback could be added here with a toast notification
      // For now, we keep the dialog open to show the error state
    }
  };

  const cancelDeleteProject = () => {
    setIsDeleteDialogOpen(false);
    setProjectToDelete(null);
  };

  const handleEditProject = (project: Project) => {
    navigate(`/${currentLanguage}/admin/projects/${project.id}/edit`);
  };

  const handleProgressUpdate = (project: Project) => {
    setSelectedProjectForProgress(project);
    setIsProgressModalOpen(true);
  };

  const handleViewProject = (project: Project) => {
    navigate(`/${currentLanguage}/projects/${project.id}`);
  };

  const handleProgressUpdateSuccess = () => {
    // The projects list will be refreshed automatically via query invalidation
    // No need to reset pagination or filters
  };

  const getStatusBadgeVariant = (status: string) => {
    switch (status) {
      case "planning":
        return "outline";
      case "in_progress":
        return "default";
      case "completed":
        return "secondary";
      case "on_hold":
        return "destructive";
      default:
        return "outline";
    }
  };

  const formatCurrency = (amount: number, currency: string) => {
    return new Intl.NumberFormat(currentLanguage, {
      style: "currency",
      currency: currency,
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString(currentLanguage);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50/30 to-purple-50/30 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6"
        >
          <div className="flex-1">
            <h1 className="text-3xl font-extrabold text-gray-900 dark:text-white bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-purple-600">
              {t("sidebar.projects")}
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mt-1">
              {t("admin.projects.subtitle")}
            </p>
          </div>
          <Link
            to={`/${currentLanguage}/admin/projects/create`}
            className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-semibold rounded-lg transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105"
          >
            <Plus className="w-5 h-5 mr-2" />
            {t("admin.projects.createNew")}
          </Link>
        </motion.div>

        {/* Filters and Search */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <Card className="bg-white dark:bg-gray-800 border-0 shadow-lg">
            <CardHeader className="bg-gradient-to-r from-indigo-50 to-blue-50 dark:from-indigo-900/20 dark:to-blue-900/20 border-b border-gray-200 dark:border-gray-700">
              <CardTitle className="flex items-center gap-2 text-xl font-bold text-gray-900 dark:text-white">
                <Filter className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                {t("admin.projects.filters")}
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="flex-1">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500 w-5 h-5" />
                    <input
                      type="text"
                      placeholder={t("admin.projects.searchPlaceholder")}
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                    />
                  </div>
                </div>
                <div className="sm:w-48">
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="w-full px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                  >
                    <option value="">{t("admin.projects.allStatuses")}</option>
                    <option value="planning">
                      {t("projects.status.planning")}
                    </option>
                    <option value="in_progress">
                      {t("projects.status.in_progress")}
                    </option>
                    <option value="completed">
                      {t("projects.status.completed")}
                    </option>
                    <option value="on_hold">
                      {t("projects.status.on_hold")}
                    </option>
                  </select>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Projects Table */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <Card className="bg-white dark:bg-gray-800 border-0 shadow-lg">
            <CardHeader className="bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 border-b border-gray-200 dark:border-gray-700">
              <CardTitle className="flex items-center gap-2 text-xl font-bold text-gray-900 dark:text-white">
                <Building2 className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                {t("admin.projects.projectList")}
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {isLoading ? (
                <div className="flex justify-center items-center py-20">
                  <div className="text-center">
                    <Loader2 className="w-12 h-12 text-blue-600 dark:text-blue-400 animate-spin mx-auto mb-4" />
                    <p className="text-gray-600 dark:text-gray-400">
                      {t("common.loading")}
                    </p>
                  </div>
                </div>
              ) : error ? (
                <div className="text-center py-20 px-4">
                  <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="max-w-md mx-auto"
                  >
                    <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
                    <p className="text-red-600 dark:text-red-400 text-lg font-semibold">
                      {t("admin.projects.errorLoading")}
                    </p>
                  </motion.div>
                </div>
              ) : projects.length === 0 ? (
                <div className="text-center py-16 px-4">
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                    className="max-w-2xl mx-auto"
                  >
                    <div className="mb-6">
                      <svg
                        className="w-24 h-24 mx-auto text-gray-400 dark:text-gray-500 mb-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={1.5}
                          d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
                        />
                      </svg>
                      <h3 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">
                        {t("projects.noProjectsTitle")}
                      </h3>
                    </div>
                    <p className="text-gray-600 dark:text-gray-400 text-lg mb-8 leading-relaxed">
                      {t("admin.projects.noProjects")}
                    </p>
                  </motion.div>
                </div>
              ) : (
                <>
                  {/* Desktop Table */}
                  <div className="hidden md:block overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-700">
                        <tr>
                          <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                            {t("admin.projects.table.title")}
                          </th>
                          <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                            {t("admin.projects.table.status")}
                          </th>
                          <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                            {t("admin.projects.table.goalAmount")}
                          </th>
                          <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                            {t("admin.projects.table.raisedAmount")}
                          </th>
                          <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                            {t("admin.projects.table.progress")}
                          </th>
                          <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                            {t("admin.projects.table.currency")}
                          </th>
                          <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                            {t("admin.projects.table.featured")}
                          </th>
                          <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                            {t("admin.projects.table.createdAt")}
                          </th>
                          <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                            {t("admin.projects.table.actions")}
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                        {projects.map((project, index) => {
                          const progressPercentage =
                            project.goal_amount > 0
                              ? (project.raised_amount / project.goal_amount) *
                                100
                              : 0;
                          const translation = project.translations?.[0];

                          return (
                            <motion.tr
                              key={project.id}
                              initial={{ opacity: 0, y: 10 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{ delay: index * 0.05 }}
                              className="hover:bg-gradient-to-r hover:from-blue-50/50 hover:to-purple-50/50 dark:hover:from-gray-800 dark:hover:to-gray-800 transition-all duration-200"
                            >
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm font-semibold text-gray-900 dark:text-white">
                                  {translation?.title || "Untitled Project"}
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <Badge
                                  variant={getStatusBadgeVariant(
                                    project.status
                                  )}
                                  className="font-medium"
                                >
                                  {t(`projects.status.${project.status}`)}
                                </Badge>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="flex items-center gap-2 text-sm font-medium text-gray-900 dark:text-white">
                                  <Target className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                                  {formatCurrency(
                                    project.goal_amount,
                                    project.currency
                                  )}
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="flex items-center gap-2 text-sm font-medium text-gray-900 dark:text-white">
                                  <TrendingUp className="w-4 h-4 text-green-600 dark:text-green-400" />
                                  {formatCurrency(
                                    project.raised_amount,
                                    project.currency
                                  )}
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="flex items-center gap-3">
                                  <div className="flex-1 bg-gray-200 dark:bg-gray-700 rounded-full h-2.5 min-w-[80px]">
                                    <motion.div
                                      initial={{ width: 0 }}
                                      animate={{
                                        width: `${Math.min(
                                          progressPercentage,
                                          100
                                        )}%`,
                                      }}
                                      transition={{
                                        duration: 0.5,
                                        delay: index * 0.05,
                                      }}
                                      className="bg-gradient-to-r from-blue-500 to-purple-500 h-2.5 rounded-full"
                                    ></motion.div>
                                  </div>
                                  <span className="text-sm font-semibold text-gray-900 dark:text-white min-w-[40px]">
                                    {progressPercentage.toFixed(0)}%
                                  </span>
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="flex items-center gap-2 text-sm font-medium text-gray-900 dark:text-white">
                                  <DollarSign className="w-4 h-4 text-green-600 dark:text-green-400" />
                                  {project.currency}
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                {project.featured ? (
                                  <Badge
                                    variant="secondary"
                                    className="flex items-center gap-1 w-fit"
                                  >
                                    <Star className="w-3 h-3 fill-yellow-400 text-yellow-400" />
                                    {t("projects.featured")}
                                  </Badge>
                                ) : (
                                  <span className="text-gray-400">-</span>
                                )}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="flex items-center gap-2 text-sm text-gray-900 dark:text-white">
                                  <Calendar className="w-4 h-4 text-gray-400 dark:text-gray-500" />
                                  {formatDate(project.created_at!)}
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                <div className="flex items-center gap-2">
                                  <motion.button
                                    whileHover={{ scale: 1.15 }}
                                    whileTap={{ scale: 0.9 }}
                                    onClick={() => handleViewProject(project)}
                                    className="p-2 rounded-lg text-blue-600 hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-900/20 transition-colors"
                                    title={t("admin.projects.view")}
                                  >
                                    <Eye className="w-4 h-4" />
                                  </motion.button>
                                  <motion.button
                                    whileHover={{ scale: 1.15 }}
                                    whileTap={{ scale: 0.9 }}
                                    onClick={() => handleEditProject(project)}
                                    className="p-2 rounded-lg text-green-600 hover:bg-green-50 dark:text-green-400 dark:hover:bg-green-900/20 transition-colors"
                                    title={t("admin.projects.edit")}
                                  >
                                    <Edit className="w-4 h-4" />
                                  </motion.button>
                                  <motion.button
                                    whileHover={{ scale: 1.15 }}
                                    whileTap={{ scale: 0.9 }}
                                    onClick={() =>
                                      handleProgressUpdate(project)
                                    }
                                    className="p-2 rounded-lg text-purple-600 hover:bg-purple-50 dark:text-purple-400 dark:hover:bg-purple-900/20 transition-colors"
                                    title={t("admin.projects.updateProgress")}
                                  >
                                    <TrendingUp className="w-4 h-4" />
                                  </motion.button>
                                  <motion.button
                                    whileHover={{ scale: 1.15 }}
                                    whileTap={{ scale: 0.9 }}
                                    onClick={() => handleDeleteProject(project)}
                                    className="p-2 rounded-lg text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20 transition-colors"
                                    title={t("admin.projects.delete")}
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </motion.button>
                                </div>
                              </td>
                            </motion.tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  {/* Mobile Cards */}
                  <div className="md:hidden space-y-4 p-6">
                    {projects.map((project, index) => {
                      const progressPercentage =
                        project.goal_amount > 0
                          ? (project.raised_amount / project.goal_amount) * 100
                          : 0;
                      const translation = project.translations?.[0];

                      return (
                        <motion.div
                          key={project.id}
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: index * 0.1 }}
                        >
                          <Card className="bg-white dark:bg-gray-800 border-0 shadow-lg hover:shadow-xl transition-shadow">
                            <CardContent className="p-6">
                              <div className="flex justify-between items-start mb-4">
                                <div className="flex-1">
                                  <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">
                                    {translation?.title || "Untitled Project"}
                                  </h3>
                                  <Badge
                                    variant={getStatusBadgeVariant(
                                      project.status
                                    )}
                                    className="font-medium"
                                  >
                                    {t(`projects.status.${project.status}`)}
                                  </Badge>
                                </div>
                                <div className="flex items-center gap-1">
                                  <motion.button
                                    whileHover={{ scale: 1.15 }}
                                    whileTap={{ scale: 0.9 }}
                                    onClick={() => handleViewProject(project)}
                                    className="p-2 rounded-lg text-blue-600 hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-900/20 transition-colors"
                                    title={t("admin.projects.view")}
                                  >
                                    <Eye className="w-4 h-4" />
                                  </motion.button>
                                  <motion.button
                                    whileHover={{ scale: 1.15 }}
                                    whileTap={{ scale: 0.9 }}
                                    onClick={() => handleEditProject(project)}
                                    className="p-2 rounded-lg text-green-600 hover:bg-green-50 dark:text-green-400 dark:hover:bg-green-900/20 transition-colors"
                                    title={t("admin.projects.edit")}
                                  >
                                    <Edit className="w-4 h-4" />
                                  </motion.button>
                                  <motion.button
                                    whileHover={{ scale: 1.15 }}
                                    whileTap={{ scale: 0.9 }}
                                    onClick={() =>
                                      handleProgressUpdate(project)
                                    }
                                    className="p-2 rounded-lg text-purple-600 hover:bg-purple-50 dark:text-purple-400 dark:hover:bg-purple-900/20 transition-colors"
                                    title={t("admin.projects.updateProgress")}
                                  >
                                    <TrendingUp className="w-4 h-4" />
                                  </motion.button>
                                  <motion.button
                                    whileHover={{ scale: 1.15 }}
                                    whileTap={{ scale: 0.9 }}
                                    onClick={() => handleDeleteProject(project)}
                                    className="p-2 rounded-lg text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20 transition-colors"
                                    title={t("admin.projects.delete")}
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </motion.button>
                                </div>
                              </div>

                              <div className="space-y-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                                <div className="flex justify-between items-center text-sm">
                                  <span className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                                    <Target className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                                    {t("admin.projects.table.goalAmount")}:
                                  </span>
                                  <span className="font-semibold text-gray-900 dark:text-white">
                                    {formatCurrency(
                                      project.goal_amount,
                                      project.currency
                                    )}
                                  </span>
                                </div>
                                <div className="flex justify-between items-center text-sm">
                                  <span className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                                    <TrendingUp className="w-4 h-4 text-green-600 dark:text-green-400" />
                                    {t("admin.projects.table.raisedAmount")}:
                                  </span>
                                  <span className="font-semibold text-gray-900 dark:text-white">
                                    {formatCurrency(
                                      project.raised_amount,
                                      project.currency
                                    )}
                                  </span>
                                </div>
                                <div className="space-y-2">
                                  <div className="flex justify-between items-center text-sm">
                                    <span className="text-gray-600 dark:text-gray-400">
                                      {t("admin.projects.table.progress")}:
                                    </span>
                                    <span className="font-bold text-gray-900 dark:text-white">
                                      {progressPercentage.toFixed(0)}%
                                    </span>
                                  </div>
                                  <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3">
                                    <motion.div
                                      initial={{ width: 0 }}
                                      animate={{
                                        width: `${Math.min(
                                          progressPercentage,
                                          100
                                        )}%`,
                                      }}
                                      transition={{
                                        duration: 0.5,
                                        delay: index * 0.1,
                                      }}
                                      className="bg-gradient-to-r from-blue-500 to-purple-500 h-3 rounded-full"
                                    ></motion.div>
                                  </div>
                                </div>
                                <div className="flex justify-between items-center text-sm pt-2">
                                  <span className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                                    <Calendar className="w-4 h-4 text-gray-400 dark:text-gray-500" />
                                    {t("admin.projects.table.createdAt")}:
                                  </span>
                                  <span className="font-medium text-gray-900 dark:text-white">
                                    {formatDate(project.created_at!)}
                                  </span>
                                </div>
                                {project.featured && (
                                  <div className="pt-2">
                                    <Badge
                                      variant="secondary"
                                      className="flex items-center gap-1 w-fit"
                                    >
                                      <Star className="w-3 h-3 fill-yellow-400 text-yellow-400" />
                                      {t("projects.featured")}
                                    </Badge>
                                  </div>
                                )}
                              </div>
                            </CardContent>
                          </Card>
                        </motion.div>
                      );
                    })}
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </motion.div>

        {/* Pagination */}
        {pagination && pagination.last_page > 1 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="flex justify-center items-center gap-2"
          >
            <Button
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              variant="outline"
              className="disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t("common.previous")}
            </Button>

            {Array.from({ length: pagination.last_page }, (_, i) => i + 1)
              .filter((page) => {
                const start = Math.max(1, currentPage - 2);
                const end = Math.min(pagination.last_page, currentPage + 2);
                return page >= start && page <= end;
              })
              .map((page) => (
                <Button
                  key={page}
                  onClick={() => setCurrentPage(page)}
                  variant={page === currentPage ? "default" : "outline"}
                  className={
                    page === currentPage
                      ? "bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white shadow-lg"
                      : ""
                  }
                >
                  {page}
                </Button>
              ))}

            <Button
              onClick={() =>
                setCurrentPage(Math.min(pagination.last_page, currentPage + 1))
              }
              disabled={currentPage === pagination.last_page}
              variant="outline"
              className="disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t("common.next")}
            </Button>
          </motion.div>
        )}

        {/* Project Progress Modal */}
        <ProjectProgressModal
          project={selectedProjectForProgress}
          isOpen={isProgressModalOpen}
          onClose={() => {
            setIsProgressModalOpen(false);
            setSelectedProjectForProgress(null);
          }}
          onSuccess={handleProgressUpdateSuccess}
        />

        {/* Delete Confirmation Dialog */}
        <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{t("admin.projects.confirmDelete")}</DialogTitle>
              <DialogDescription>
                {t("admin.projects.confirmDeleteDescription", {
                  title:
                    projectToDelete?.translations?.[0]?.title || "this project",
                })}
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={cancelDeleteProject}
                disabled={deleteProjectMutation.isPending}
              >
                {t("common.cancel")}
              </Button>
              <Button
                variant="destructive"
                onClick={confirmDeleteProject}
                disabled={deleteProjectMutation.isPending}
              >
                {deleteProjectMutation.isPending
                  ? t("common.deleting")
                  : t("admin.projects.delete")}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

Projects.displayName = "Projects";

export default Projects;
